<template>
  <div class="mes-agent-answer">
    <div class="mes-agent-answer__head">
      <el-tag v-if="answer.intent" size="small" effect="plain" round>{{
        answer.intent.title
      }}</el-tag>
      <el-tag
        v-if="answer.status !== 'unsupported'"
        size="small"
        :type="confidenceTag.type"
        effect="light"
        round
      >
        신뢰도 {{ confidenceTag.label }} · {{ Math.round(answer.confidence.score * 100) }}%
      </el-tag>
      <el-tag v-if="answer.llm?.used" size="small" type="warning" effect="plain" round
        >{{ answer.llm.model }} 작성 · 수치 대조</el-tag
      >
    </div>

    <p class="mes-agent-answer__text">{{ answer.answer }}</p>

    <div v-if="answer.warnings.length" class="mes-agent-answer__warnings">
      <el-alert
        v-for="(w, i) in answer.warnings"
        :key="i"
        :title="w.message"
        :type="w.level === 'error' ? 'error' : w.level === 'warning' ? 'warning' : 'info'"
        show-icon
        :closable="false"
      />
    </div>

    <!-- 처리 내역 -->
    <div v-if="answer.steps.length" class="mes-agent-steps">
      <button type="button" class="mes-agent-steps__toggle" @click="stepsOpen = !stepsOpen">
        <el-icon class="mes-agent-steps__chev" :class="{ 'is-open': stepsOpen }"
          ><ArrowRight
        /></el-icon>
        처리 내역
        <span class="mes-agent-steps__meta"
          >{{ answer.steps.length }}단계 · {{ fmtMs(answer.meta.elapsedMs) }}</span
        >
        <span class="mes-agent-steps__flow">
          <span
            v-for="s in answer.steps"
            :key="s.key"
            class="mes-agent-steps__dot"
            :class="`is-${s.status}`"
            :title="s.label"
          />
        </span>
      </button>
      <ol v-if="stepsOpen" class="mes-agent-steps__list">
        <li
          v-for="(s, idx) in answer.steps"
          :key="s.key"
          class="mes-agent-step"
          :class="`is-${s.status}`"
        >
          <div class="mes-agent-step__row" @click="toggleStep(s.key)">
            <el-icon class="mes-agent-step__icon">
              <CircleCheckFilled v-if="s.status === 'done'" />
              <WarningFilled v-else-if="s.status === 'warn'" />
              <CircleCloseFilled v-else />
            </el-icon>
            <span class="mes-agent-step__no">{{ idx + 1 }}</span>
            <span class="mes-agent-step__label">{{ s.label }}</span>
            <span class="mes-agent-step__summary">{{ s.summary }}</span>
            <span class="mes-agent-step__ms">{{ fmtMs(s.elapsedMs) }}</span>
          </div>
          <div v-if="openSteps.has(s.key) && hasBody(s)" class="mes-agent-step__body">
            <ul v-if="s.details?.length" class="mes-agent-step__details">
              <li v-for="(d, i) in s.details" :key="i">{{ d }}</li>
            </ul>
            <div v-if="s.tables?.length" class="mes-agent-step__tables">
              <div v-for="t in s.tables" :key="t.name" class="mes-agent-step__table">
                <code>{{ t.name }}</code>
                <span>{{ t.description }}</span>
                <small>{{ t.columns.join(', ') }}</small>
              </div>
            </div>
            <div v-if="s.sql" class="mes-agent-sql">
              <div class="mes-agent-sql__bar">
                <span>SQL · PostgreSQL · 읽기 전용</span>
                <el-button link size="small" :icon="DocumentCopy" @click="copy(s.sql)"
                  >복사</el-button
                >
              </div>
              <pre>{{ s.sql }}</pre>
            </div>
            <table v-if="s.checks?.length" class="mes-agent-checks">
              <tbody>
                <tr v-for="c in s.checks" :key="c.label" :class="`is-${c.status}`">
                  <td class="mes-agent-checks__mark">
                    {{ c.status === 'pass' ? '✓' : c.status === 'warn' ? '!' : '✕' }}
                  </td>
                  <td class="mes-agent-checks__label">{{ c.label }}</td>
                  <td>{{ c.detail }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </li>
      </ol>
    </div>

    <!-- 결과 -->
    <div v-if="answer.columns.length && answer.status !== 'unsupported'" class="mes-agent-result">
      <div class="mes-agent-result__bar">
        <el-segmented
          v-if="answer.viz && answer.rows.length"
          v-model="view"
          :options="viewOptions"
          size="small"
        />
        <span class="mes-agent-result__title">{{
          answer.meta.period
            ? `${answer.meta.period.label} · ${answer.meta.period.from} ~ ${answer.meta.period.to}`
            : ''
        }}</span>
      </div>
      <MesAgentResultChart
        v-if="view === 'chart' && answer.viz && answer.rows.length"
        :viz="answer.viz"
        :rows="answer.chartRows ?? answer.rows"
      />
      <el-table
        v-else
        :data="answer.rows"
        border
        stripe
        max-height="340"
        class="mes-agent-result__table"
        empty-text="조회 결과 없음"
      >
        <el-table-column
          v-for="c in answer.columns"
          :key="c.key"
          :prop="c.key"
          :label="c.label"
          :align="c.type === 'number' ? 'right' : 'left'"
          :min-width="colWidth(c)"
          show-overflow-tooltip
        >
          <template #default="{ row }">
            <el-tag
              v-if="STATUS_TONE[String(row[c.key])]"
              size="small"
              :type="STATUS_TONE[String(row[c.key])]"
              effect="light"
              >{{ row[c.key] }}</el-tag
            >
            <span v-else :class="{ 'is-null': row[c.key] == null }">{{ fmtCell(row[c.key]) }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="answer.insight" class="mes-agent-evidence">
        <div class="mes-agent-evidence__head">
          <el-icon><DataAnalysis /></el-icon>
          <b>{{ answer.insight.model }}</b> v{{ answer.insight.version }} ·
          {{ answer.insight.operationLabel }}
          <span class="mes-agent-evidence__run">실행 ID {{ answer.insight.runId }}</span>
        </div>
        <ul>
          <li v-for="(e, i) in answer.evidence" :key="i">{{ e }}</li>
        </ul>
      </div>

      <div v-if="answer.algorithm" class="mes-agent-algo">
        <div class="mes-agent-algo__head">
          <span class="mes-agent-algo__label">분석 알고리즘</span>
          <b>{{ answer.algorithm.selected.name }}</b>
          <el-tag
            size="small"
            :type="answer.algorithm.mode === 'auto' ? 'success' : 'warning'"
            effect="light"
            round
            >{{ answer.algorithm.mode === 'auto' ? '자동 선택' : '사용자 지정' }}</el-tag
          >
          <span class="mes-agent-algo__model">{{ answer.algorithm.model }}</span>
        </div>
        <p class="mes-agent-algo__reason">{{ answer.algorithm.reason }}</p>
        <table class="mes-agent-algo__table">
          <thead>
            <tr>
              <th>후보 알고리즘</th>
              <th class="num">지표</th>
              <th>비교 내용</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="c in answer.algorithm.candidates"
              :key="c.key"
              :class="{ 'is-selected': c.selected }"
            >
              <td>
                <span class="mes-agent-algo__mark">{{ c.selected ? '✓' : '' }}</span
                >{{ c.name }}
              </td>
              <td class="num">{{ c.score ?? '-' }}</td>
              <td class="note">{{ c.note }}</td>
            </tr>
          </tbody>
        </table>
        <div class="mes-agent-algo__metric">지표: {{ answer.algorithm.metric }}</div>
      </div>

      <div class="mes-agent-result__foot">
        <span
          >기준 시점 {{ fmtAsOf(answer.meta.asOf) }} ·
          <template v-if="answer.insight && answer.inputRows !== answer.meta.rowCount"
            >입력 {{ answer.inputRows.toLocaleString() }}건 → {{ answer.insight.model }} 결과
            {{ answer.meta.rowCount.toLocaleString() }}건</template
          ><template v-else>조회 {{ answer.meta.rowCount.toLocaleString() }}건</template> ·
          {{ answer.meta.source }}</span
        >
        <el-button-group>
          <el-button size="small" :icon="Download" :disabled="!answer.rows.length" @click="onCsv"
            >CSV</el-button
          >
          <el-button size="small" :icon="Download" :disabled="!answer.rows.length" @click="onXlsx"
            >Excel</el-button
          >
        </el-button-group>
      </div>
    </div>

    <div v-if="answer.followUps.length" class="mes-agent-answer__follow">
      <button
        v-for="f in answer.followUps"
        :key="f"
        type="button"
        class="mes-agent-chip"
        @click="emit('ask', f)"
      >
        {{ f }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { ElMessage } from 'element-plus';
import {
  ArrowRight,
  CircleCheckFilled,
  CircleCloseFilled,
  DataAnalysis,
  DocumentCopy,
  Download,
  WarningFilled,
} from '@element-plus/icons-vue';

import type { AgentAnswer, AgentColumn, AgentStep } from '../../api/agent';
import { exportCsv, exportXlsx } from '../../utils/agent-export';
import MesAgentResultChart from './MesAgentResultChart.vue';

const props = defineProps<{ answer: AgentAnswer }>();
const emit = defineEmits<{ ask: [question: string] }>();

const stepsOpen = ref(false);
const openSteps = ref(new Set<string>(['generate', 'validate', 'verify']));
const view = ref<'chart' | 'table'>(props.answer.viz ? 'chart' : 'table');
const viewOptions = [
  { label: '차트', value: 'chart' },
  { label: '표', value: 'table' },
];

const confidenceTag = computed(() => {
  const l = props.answer.confidence.level;
  return l === 'high'
    ? { type: 'success' as const, label: '높음' }
    : l === 'medium'
      ? { type: 'warning' as const, label: '보통' }
      : { type: 'danger' as const, label: '낮음' };
});

function toggleStep(key: string): void {
  const next = new Set(openSteps.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  openSteps.value = next;
}
/** 한글 1자 ≈ 13px, 그 외 ≈ 7.5px 로 머리글·값 중 긴 쪽 기준 최소 너비 */
function textPx(s: string): number {
  let w = 0;
  for (const ch of s) w += /[\u3131-\uD79D]/.test(ch) ? 13 : 7.5;
  return w;
}
function colWidth(c: AgentColumn): number {
  const sample = props.answer.rows.slice(0, 40).map((r) => fmtCell(r[c.key]));
  const w = Math.max(textPx(c.label), ...sample.map(textPx)) + 28;
  // 원인·비고처럼 문장이 들어가는 열은 넓게(잘리면 툴팁)
  const cap = /원인|비고|사유|근거/.test(c.label) ? 460 : 220;
  return Math.round(Math.min(cap, Math.max(64, w)));
}
/** 판정·상태 값 강조 */
const STATUS_TONE: Record<string, 'danger' | 'warning' | 'success' | 'info' | 'primary'> = {
  경고: 'danger',
  주의: 'warning',
  정상: 'success',
  지연: 'danger',
  '지연 납품': 'warning',
  미출고: 'danger',
  준수: 'success',
  '긴급 보충': 'danger',
  '보충 필요': 'warning',
  적정: 'success',
  과잉: 'info',
  매뉴얼: 'primary',
};
const hasBody = (s: AgentStep): boolean =>
  Boolean(s.details?.length || s.tables?.length || s.sql || s.checks?.length);
const fmtMs = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(2)}초` : `${ms}ms`);
const fmtCell = (v: string | number | null | undefined): string =>
  v == null
    ? 'NULL'
    : typeof v === 'number'
      ? v.toLocaleString('ko-KR', { maximumFractionDigits: 2 })
      : String(v);
function fmtAsOf(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 9 * 3600_000);
  return d.toISOString().slice(0, 19).replace('T', ' ');
}
async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    ElMessage.success('SQL 을 복사했습니다');
  } catch {
    ElMessage.warning('복사 권한이 없습니다');
  }
}
const fileBase = (): string =>
  `AX-Agent_${props.answer.intent?.title ?? '결과'}_${fmtAsOf(props.answer.meta.asOf).slice(0, 10)}`.replace(
    /[\\/:*?"<>|\s]/g,
    '_',
  );
const onCsv = (): void => exportCsv(props.answer.columns, props.answer.rows, fileBase());
const onXlsx = (): void =>
  exportXlsx(
    props.answer.columns,
    props.answer.rows,
    fileBase(),
    props.answer.intent?.title ?? 'Agent 결과',
  );

defineExpose({ openSteps: () => (stepsOpen.value = true) });
</script>

<style scoped>
.mes-agent-answer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mes-agent-answer__head {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.mes-agent-answer__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: var(--el-text-color-primary);
  white-space: pre-wrap;
}
.mes-agent-answer__warnings {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mes-agent-steps {
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  background: var(--el-fill-color-extra-light);
}
.mes-agent-steps__toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  cursor: pointer;
}
.mes-agent-steps__chev {
  transition: transform 0.15s;
}
.mes-agent-steps__chev.is-open {
  transform: rotate(90deg);
}
.mes-agent-steps__meta {
  font-weight: 400;
  color: var(--el-text-color-secondary);
}
.mes-agent-steps__flow {
  display: inline-flex;
  gap: 4px;
  margin-left: auto;
}
.mes-agent-steps__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--el-color-success);
}
.mes-agent-steps__dot.is-warn {
  background: var(--el-color-warning);
}
.mes-agent-steps__dot.is-error {
  background: var(--el-color-danger);
}
.mes-agent-steps__list {
  margin: 0;
  padding: 0 10px 10px;
  list-style: none;
}
.mes-agent-step {
  border-top: 1px dashed var(--el-border-color-lighter);
}
.mes-agent-step__row {
  display: grid;
  grid-template-columns: 18px 18px auto 1fr auto;
  align-items: center;
  gap: 6px;
  padding: 7px 0;
  font-size: 13px;
  cursor: pointer;
}
.mes-agent-step__icon {
  color: var(--el-color-success);
}
.mes-agent-step.is-warn .mes-agent-step__icon {
  color: var(--el-color-warning);
}
.mes-agent-step.is-error .mes-agent-step__icon {
  color: var(--el-color-danger);
}
.mes-agent-step__no {
  font-size: 11px;
  color: var(--el-text-color-placeholder);
}
.mes-agent-step__label {
  font-weight: 600;
  white-space: nowrap;
}
.mes-agent-step__summary {
  overflow: hidden;
  color: var(--el-text-color-regular);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mes-agent-step__ms {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  font-variant-numeric: tabular-nums;
}
.mes-agent-step__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 0 10px 42px;
}
.mes-agent-step__details {
  margin: 0;
  padding-left: 16px;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--el-text-color-regular);
}
.mes-agent-step__tables {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.mes-agent-step__table {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: baseline;
  font-size: 12.5px;
}
.mes-agent-step__table code {
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
}
.mes-agent-step__table small {
  color: var(--el-text-color-secondary);
}
.mes-agent-sql {
  overflow: hidden;
  border-radius: 6px;
  background: #0f172a;
}
.mes-agent-sql__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 10px;
  font-size: 11px;
  color: #94a3b8;
  background: #1e293b;
}
.mes-agent-sql pre {
  max-height: 200px;
  margin: 0;
  padding: 10px 12px;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.55;
  color: #e2e8f0;
}
.mes-agent-checks {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}
.mes-agent-checks td {
  padding: 4px 6px;
  border-bottom: 1px solid var(--el-border-color-extra-light);
  vertical-align: top;
}
.mes-agent-checks__mark {
  width: 18px;
  font-weight: 700;
  color: var(--el-color-success);
  text-align: center;
}
.mes-agent-checks tr.is-warn .mes-agent-checks__mark {
  color: var(--el-color-warning);
}
.mes-agent-checks tr.is-fail .mes-agent-checks__mark {
  color: var(--el-color-danger);
}
.mes-agent-checks__label {
  width: 110px;
  font-weight: 600;
  white-space: nowrap;
}

.mes-agent-result {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  background: var(--el-bg-color);
}
.mes-agent-result__bar {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mes-agent-result__title {
  margin-left: auto;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.mes-agent-result__table .is-null {
  color: var(--el-color-danger);
  font-style: italic;
}
.mes-agent-evidence {
  padding: 8px 10px;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  background: color-mix(in srgb, #f59e0b 7%, transparent);
  font-size: 12.5px;
}
.mes-agent-evidence__head {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  color: var(--el-text-color-primary);
}
.mes-agent-evidence__run {
  margin-left: auto;
  font-size: 11px;
  color: var(--el-text-color-secondary);
  font-family: ui-monospace, monospace;
}
.mes-agent-evidence ul {
  margin: 0;
  padding-left: 18px;
  line-height: 1.6;
  color: var(--el-text-color-regular);
}
.mes-agent-algo {
  padding: 10px 12px;
  border: 1px solid color-mix(in srgb, #d97706 35%, transparent);
  border-radius: 8px;
  background: color-mix(in srgb, #d97706 4%, var(--el-bg-color));
  font-size: 12.5px;
}
.mes-agent-algo__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  color: var(--el-text-color-primary);
}
.mes-agent-algo__label {
  font-size: 11px;
  font-weight: 700;
  color: #b45309;
}
.mes-agent-algo__model {
  margin-left: auto;
  font-size: 11px;
  color: var(--el-text-color-secondary);
}
.mes-agent-algo__reason {
  margin: 6px 0 8px;
  line-height: 1.55;
  color: var(--el-text-color-regular);
}
.mes-agent-algo__table {
  width: 100%;
  border-collapse: collapse;
}
.mes-agent-algo__table th,
.mes-agent-algo__table td {
  padding: 4px 6px;
  border-bottom: 1px solid var(--el-border-color-extra-light);
  text-align: left;
  vertical-align: top;
}
.mes-agent-algo__table th {
  font-size: 11px;
  font-weight: 600;
  color: var(--el-text-color-secondary);
}
.mes-agent-algo__table .num {
  width: 64px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.mes-agent-algo__table .note {
  color: var(--el-text-color-secondary);
}
.mes-agent-algo__table tr.is-selected td {
  background: color-mix(in srgb, #d97706 10%, transparent);
  font-weight: 600;
  color: var(--el-text-color-primary);
}
.mes-agent-algo__mark {
  display: inline-block;
  width: 14px;
  color: #d97706;
}
.mes-agent-algo__metric {
  margin-top: 6px;
  font-size: 11px;
  color: var(--el-text-color-secondary);
}
.mes-agent-result__foot .el-button-group {
  display: inline-flex;
  flex: none;
}
.mes-agent-result__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.mes-agent-answer__follow {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.mes-agent-chip {
  padding: 5px 10px;
  border: 1px solid var(--el-border-color);
  border-radius: 999px;
  background: var(--el-bg-color);
  font-size: 12.5px;
  color: var(--el-text-color-regular);
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s;
}
.mes-agent-chip:hover {
  border-color: var(--el-color-primary);
  color: var(--el-color-primary);
}
</style>
